<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software; you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with this program;
 * if not, write to the Free Software Foundation, Inc., 51 Franklin Street, Fifth Floor,
 * Boston, MA  02110-1301, USA
 */
 -->

<template>
  <div
    :class="wrapperClasses"
    :style="wrapperStyles"
    class="dashboard-stacked-bar-chart"
  >
    <div ref="canvasWrapperRef" class="dashboard-stacked-bar-chart-canvas">
      <canvas ref="canvasRef"></canvas>
    </div>
    <Teleport to="body">
      <div
        v-if="selectedBucket && popupPosition"
        ref="popupRef"
        class="dashboard-stacked-bar-chart-popup"
        :class="{'--below': popupPlacement === 'below'}"
        :style="popupStyle"
        @click.stop
      >
        <oxd-text tag="p" class="dashboard-stacked-bar-chart-popup-title">
          {{ selectedBucket.label }}
        </oxd-text>
        <div
          v-for="row in selectedBucket.rows"
          :key="row.label"
          class="dashboard-stacked-bar-chart-popup-item"
        >
          <span
            class="dashboard-stacked-bar-chart-popup-swatch"
            :style="{backgroundColor: row.color}"
          ></span>
          <oxd-text tag="p" class="dashboard-stacked-bar-chart-popup-row">
            {{ row.label }}: {{ row.value }}
          </oxd-text>
        </div>
        <oxd-text tag="p" class="dashboard-stacked-bar-chart-popup-total">
          Total: {{ selectedBucket.total }}
        </oxd-text>
      </div>
    </Teleport>
  </div>
</template>

<script>
import {ref, computed, watch, onMounted, onBeforeUnmount, nextTick} from 'vue';
import {
  Chart,
  Legend,
  Tooltip,
  BarController,
  BarElement,
  LinearScale,
  CategoryScale,
} from 'chart.js';

Chart.register(
  Legend,
  Tooltip,
  BarController,
  BarElement,
  LinearScale,
  CategoryScale,
);

const BAR_TRACK_COLOR = 'rgb(246, 245, 251)';

const dashboardBarTrackPlugin = {
  id: 'dashboardBarTrack',
  beforeDraw(chart) {
    if (chart.config.type !== 'bar') {
      return;
    }

    const {ctx, scales} = chart;
    const yScale = scales.y;
    const meta = chart.getDatasetMeta(0);

    if (!yScale || !meta?.data?.length) {
      return;
    }

    ctx.save();
    ctx.globalCompositeOperation = 'destination-over';
    ctx.fillStyle = BAR_TRACK_COLOR;

    const baseline = yScale.getPixelForValue(0);

    meta.data.forEach((bar) => {
      ctx.fillRect(
        bar.x - bar.width / 2,
        yScale.top,
        bar.width,
        baseline - yScale.top,
      );
    });

    ctx.restore();
  },
};

export default {
  name: 'DashboardStackedBarChart',

  props: {
    labels: {
      type: Array,
      default: () => [],
    },
    series: {
      type: Array,
      default: () => [],
    },
    yAxisLabel: {
      type: String,
      default: '',
    },
    xAxisLabel: {
      type: String,
      default: '',
    },
    showLegend: {
      type: Boolean,
      default: false,
    },
    wrapperClasses: {
      type: [String, Object, Array],
      default: null,
    },
    wrapperStyles: {
      type: Object,
      default: () => ({}),
    },
  },

  setup(props) {
    const canvasRef = ref(null);
    const canvasWrapperRef = ref(null);
    const popupRef = ref(null);
    const selectedBucketIndex = ref(null);
    const popupPosition = ref(null);
    const popupPlacement = ref('above');
    let chartInstance = null;
    let resizeObserver = null;

    const selectedBucket = computed(() => {
      if (selectedBucketIndex.value === null) {
        return null;
      }

      const index = selectedBucketIndex.value;
      const label = props.labels[index];
      if (label === undefined) {
        return null;
      }

      const rows = props.series
        .map((item) => ({
          label: item.label,
          value: item.values[index] ?? 0,
          color: item.color,
        }))
        .filter((row) => row.value > 0);

      return {
        label,
        rows,
        total: rows.reduce((sum, row) => sum + row.value, 0),
      };
    });

    const popupStyle = computed(() => {
      if (!popupPosition.value) {
        return {};
      }

      return {
        left: `${popupPosition.value.left}px`,
        top: `${popupPosition.value.top}px`,
      };
    });

    const clearPopup = () => {
      selectedBucketIndex.value = null;
      popupPosition.value = null;
      popupPlacement.value = 'above';
    };

    const adjustPopupPlacement = () => {
      const popupEl = popupRef.value;
      const canvas = canvasRef.value;

      if (!popupEl || !popupPosition.value || !canvas) {
        return;
      }

      const {anchorTop, anchorBottom} = popupPosition.value;
      const popupHeight = popupEl.offsetHeight;
      const halfWidth = popupEl.offsetWidth / 2;
      const edgePadding = 8;
      const left = Math.max(
        halfWidth + edgePadding,
        Math.min(
          window.innerWidth - halfWidth - edgePadding,
          popupPosition.value.left,
        ),
      );

      let top = anchorTop;
      let placement = 'above';

      if (anchorTop - popupHeight - edgePadding < edgePadding) {
        placement = 'below';
        top = anchorBottom;
      }

      popupPlacement.value = placement;
      popupPosition.value = {
        ...popupPosition.value,
        left,
        top,
      };
    };

    const resolveBucketFromClick = (chart, event, elements) => {
      if (elements.length) {
        return {
          index: elements[0].index,
          element: elements[0].element,
        };
      }

      if (!chart) {
        return null;
      }

      const hits = chart.getElementsAtEventForMode(
        event,
        'index',
        {intersect: false},
        false,
      );

      if (!hits.length) {
        return null;
      }

      return {
        index: hits[0].index,
        element: hits[0].element,
      };
    };

    const showPopupForBucket = async (chart, index) => {
      const canvas = canvasRef.value;
      const meta = chart?.getDatasetMeta(0);
      const element = meta?.data?.[index];

      if (!canvas || !element || !chart?.scales?.y) {
        return;
      }

      const rect = canvas.getBoundingClientRect();
      const yScale = chart.scales.y;
      const baseline = yScale.getPixelForValue(0);
      const trackTop = yScale.top;
      const barHeight = Math.abs(element.base - element.y);
      const useTrackAnchor = barHeight < 4;
      const anchorY = useTrackAnchor ? (trackTop + baseline) / 2 : element.y;

      popupPosition.value = {
        left: rect.left + element.x,
        top: rect.top + anchorY,
        anchorTop: rect.top + anchorY,
        anchorBottom: rect.top + (useTrackAnchor ? baseline : element.base),
      };
      popupPlacement.value = 'above';

      await nextTick();
      adjustPopupPlacement();
    };

    const handleChartClick = async (event, elements, chart) => {
      const activeChart = chart ?? chartInstance;
      const bucket = resolveBucketFromClick(activeChart, event, elements);

      if (!bucket) {
        clearPopup();
        return;
      }

      const {index} = bucket;
      if (selectedBucketIndex.value === index) {
        clearPopup();
        return;
      }

      selectedBucketIndex.value = index;
      await showPopupForBucket(activeChart, index);
    };

    const dismissOnViewportChange = (event) => {
      if (popupRef.value?.contains(event.target)) {
        return;
      }

      clearPopup();
    };

    const buildChartData = () => ({
      labels: props.labels,
      datasets: props.series.map((item) => ({
        label: item.label,
        data: item.values,
        backgroundColor: item.color,
        borderColor: item.color,
        borderWidth: 1,
        maxBarThickness: 48,
      })),
    });

    const buildChartOptions = () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      onClick: handleChartClick,
      onHover: (event, elements, chart) => {
        if (!chart?.canvas) {
          return;
        }
        const bucket = resolveBucketFromClick(chart, event, elements);
        chart.canvas.style.cursor = bucket ? 'pointer' : 'default';
      },
      interaction: {
        mode: 'index',
        intersect: false,
      },
      plugins: {
        legend: {
          display: props.showLegend,
        },
        tooltip: {
          enabled: false,
        },
        oxdChartLabels: false,
        OxdBarChartFill: false,
      },
      scales: {
        x: {
          stacked: true,
          grid: {
            display: false,
          },
          title: {
            display: !!props.xAxisLabel,
            text: props.xAxisLabel,
          },
          ticks: {
            maxRotation: 0,
            autoSkip: true,
            maxTicksLimit: 24,
          },
        },
        y: {
          stacked: true,
          beginAtZero: true,
          grace: 0,
          title: {
            display: !!props.yAxisLabel,
            text: props.yAxisLabel,
          },
          ticks: {
            precision: 0,
          },
        },
      },
    });

    const observeChartResize = () => {
      if (typeof ResizeObserver === 'undefined' || !canvasWrapperRef.value) {
        return;
      }

      resizeObserver?.disconnect();
      resizeObserver = new ResizeObserver(() => {
        chartInstance?.resize();
      });
      resizeObserver.observe(canvasWrapperRef.value);
    };

    const renderChart = () => {
      if (!canvasRef.value) {
        return;
      }

      const chartData = buildChartData();
      const chartOptions = buildChartOptions();

      if (chartInstance) {
        chartInstance.data = chartData;
        chartInstance.options = chartOptions;
        chartInstance.update('none');
        nextTick(() => {
          chartInstance?.resize();
        });
        return;
      }

      chartInstance = new Chart(canvasRef.value, {
        type: 'bar',
        data: chartData,
        options: chartOptions,
        plugins: [dashboardBarTrackPlugin],
      });
      observeChartResize();
      nextTick(() => {
        chartInstance?.resize();
      });
    };

    const destroyChart = () => {
      resizeObserver?.disconnect();
      resizeObserver = null;
      if (chartInstance) {
        chartInstance.destroy();
        chartInstance = null;
      }
    };

    watch(
      () => [
        props.labels,
        props.series,
        props.yAxisLabel,
        props.xAxisLabel,
        props.showLegend,
      ],
      () => {
        clearPopup();
        renderChart();
      },
      {deep: true},
    );

    onMounted(() => {
      window.addEventListener('scroll', dismissOnViewportChange, true);
      window.addEventListener('resize', dismissOnViewportChange);
      renderChart();
    });

    onBeforeUnmount(() => {
      window.removeEventListener('scroll', dismissOnViewportChange, true);
      window.removeEventListener('resize', dismissOnViewportChange);
      destroyChart();
    });

    return {
      canvasRef,
      canvasWrapperRef,
      popupRef,
      selectedBucket,
      popupPosition,
      popupPlacement,
      popupStyle,
      renderChart,
      destroyChart,
    };
  },
};
</script>

<style lang="scss" scoped>
.dashboard-stacked-bar-chart {
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.dashboard-stacked-bar-chart-canvas {
  flex: 1;
  min-height: 0;
  height: 100%;
  position: relative;
}

.dashboard-stacked-bar-chart-popup {
  position: fixed;
  z-index: 1000;
  min-width: 10rem;
  max-width: 16rem;
  max-height: min(280px, 70vh);
  overflow-y: auto;
  padding: 0.5rem 0.75rem;
  border-radius: 4px;
  background-color: $oxd-white-color;
  border: 1px solid $oxd-interface-gray-lighten-2-color;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  transform: translate(-50%, calc(-100% - 8px));
  pointer-events: auto;
  font-family: $oxd-font-family;

  &::after {
    content: '';
    position: absolute;
    left: 50%;
    bottom: -6px;
    width: 10px;
    height: 10px;
    background-color: $oxd-white-color;
    border-right: 1px solid $oxd-interface-gray-lighten-2-color;
    border-bottom: 1px solid $oxd-interface-gray-lighten-2-color;
    transform: translateX(-50%) rotate(45deg);
  }

  &.--below {
    transform: translate(-50%, 8px);

    &::after {
      top: -6px;
      bottom: auto;
      border-right: none;
      border-bottom: none;
      border-left: 1px solid $oxd-interface-gray-lighten-2-color;
      border-top: 1px solid $oxd-interface-gray-lighten-2-color;
    }
  }
}

.dashboard-stacked-bar-chart-popup-title {
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.dashboard-stacked-bar-chart-popup-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.25rem;
}

.dashboard-stacked-bar-chart-popup-row {
  font-size: 0.875rem;
}

.dashboard-stacked-bar-chart-popup-swatch {
  width: 0.75rem;
  height: 0.75rem;
  border-radius: 2px;
  flex-shrink: 0;
}

.dashboard-stacked-bar-chart-popup-total {
  font-weight: 600;
  font-size: 0.875rem;
  margin-top: 0.25rem;
}
</style>
